import { createContext, useContext } from 'react';
import type { ConversationTranscript } from '@/entities/conversations';
import type { TimeDisplayFormat } from '@/entities/settings';
import type { SpeakerSide } from '../speaker';
import type { TranscriptEditorState } from './useTranscriptEditorState';

/**
 * Общее для строк и форм ленты режима редактирования: состояние режима и сведения о расшифровке,
 * по которым строки подписывают перебивания и предупреждают об удалении.
 */
export type TranscriptEditorContextValue = TranscriptEditorState & {
  conversationId: number;
  /** Реплики расшифровки по id */
  phrasesById: Map<number, ConversationTranscript>;
  /** Число перебиваний, в которых реплика перебитая или перебивающая, по id реплики */
  interruptionCountByPhraseId: Map<number, number>;
  /** Сторона ленты участников диалога по id — ею окрашена рамка формы реплики выбранного говорящего */
  personSideById: Map<number, SpeakerSide>;
  /** Формат показа времени расшифровки: в нём показаны границы записей в ленте */
  timeFormat: TimeDisplayFormat;
  /** Класс размера шрифта ленты: им набран текст реплик в строках, но не поля открытой формы */
  phraseTextClassName: string;
  /** Длительность записи разговора, мс: от неё зависит, есть ли в поле времени часы */
  recordingDurationMs: number;
};

/** Контекст ленты режима редактирования: его даёт лента, читают её строки и формы */
export const TranscriptEditorContext = createContext<TranscriptEditorContextValue | null>(null);

/**
 * Контекст ленты режима редактирования; вне неё — ошибка.
 */
export const useTranscriptEditorContext = (): TranscriptEditorContextValue => {
  const value = useContext(TranscriptEditorContext);
  if (value === null) {
    throw new Error('useTranscriptEditorContext must be used within TranscriptEditorContext');
  }
  return value;
};
